import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Badge from '@/components/ui/custom/dataDisplay/Badge';
import { IconWithText } from '@/components/ui/custom/dataDisplay/IconWithText';
import CheckGroup, {
  CheckOption,
} from '@/components/ui/custom/dataInput/CheckGroup';
import { InfinityListOptions } from '@/components/ui/custom/InfinityList';
import Popup from '@/components/ui/custom/Popup';
import { LoadingIcon } from '@/components/icons';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/20/solid';
import { Placement } from '@popperjs/core';
import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';

type PopupProps = {
  isOpen?: boolean;
  toggle?: () => void;
  close?: () => void;
  open?: () => void;
};

type CourseCheckBoxFilterProps = {
  text: string;
  options: CheckOption[];
  placement?: Placement;
  offset?: [number, number];
  className?: Pick<ClassNameComponent, 'reference'>;
  isLoading?: boolean;
} & Omit<InfinityListOptions, 'className' | 'onScroll'> &
  PopupProps;

export default function CourseCheckBoxFilter({
  text,
  options,
  hasNextPage,
  hasPreviousPage,
  isFetchingNextPage,
  isFetchingPreviousPage,
  onReachEnd,
  onReachStart,
  scroll,
  placement,
  offset,
  className,
  toggle,
  isOpen,
  close,
  open,
  isLoading,
}: CourseCheckBoxFilterProps) {
  const checkedOptionsCount = options.reduce(
    (count, { checked }) => (checked ? count + 1 : count),
    0,
  );
  const Icon = checkedOptionsCount ? FunnelIconSolid : FunnelIcon;
  const reference = useMemo(
    () => (
      <IconWithText
        icon={
          <Badge
            count={checkedOptionsCount}
            color="info"
            position="-top-1 -right-0.5"
          >
            <Icon className="w-4 h-4 text-secondary" strokeWidth={2.5} />
          </Badge>
        }
        text={text}
        className={{
          text: 'text-base-content font-rubik text-14 lg:text-16',
          base: 'gap-1.5 flex-row-reverse cursor-pointer',
        }}
      />
    ),
    [Icon, checkedOptionsCount, text],
  );

  return (
    <Popup
      reference={reference}
      className={className}
      placement={placement}
      offset={offset}
      opened={isOpen}
      onToggle={toggle}
      onClose={close}
      onOpen={open}
      testId="coursePopup"
    >
      <div
        className={twJoin(
          'bg-base-100 border border-neutral-content rounded-md p-3 gap-1.5 max-h-80 overflow-y-auto',
          scrollBarYClassName,
        )}
      >
        <ConditionalRenderer
          condition={!isLoading}
          fallback={<LoadingIcon className="w-6 m-2 text-primary" />}
        >
          <CheckGroup
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetchingPreviousPage={isFetchingPreviousPage}
            onReachStart={onReachStart}
            onReachEnd={onReachEnd}
            options={options}
            scroll={scroll}
          />
        </ConditionalRenderer>
      </div>
    </Popup>
  );
}
